import { ApplicationConfig, ExcelColumnConfig, ExcelExportFeatures } from '../../redux/application/application.types';
import { JobType } from '../../redux/jobs/jobs.types';
import { mergeJobConfig } from '../../helper/JobConfigHelper';
import { CANONICAL_COLUMN_ORDER } from './ColumnOrder';
import { extractExportColumnValue, ExportColumnValue } from './ColumnValueResolver';

// Client-side format sentinels (not persisted, not compared against backend format ids).
export const AUTO_FORMAT_ID = 'auto';
export const CUSTOM_FORMAT_ID = 'custom';

export interface ResolvedExportConfig {
    formatId: string;
    additionalColumns: ExcelColumnConfig[];
    features: ExcelExportFeatures;
}

// Optional columns eligible for auto-inclusion. Included only when their resolved
// export value varies across jobs. Variance is computed via the SAME resolver used
// to write the export (extractExportColumnValue), so OBIS-derived units, customMeterType,
// and per-task values are honored consistently — no divergent dot-path heuristics.
const OPTIONAL_AUTO_COLUMN_KEYS: string[] = [
    'meter.address.street',
    'meter.address.zip',
    'meter.address.city',
    'meter.address.objectKey',
    'meter.address.locationDescription',
    'meter.address.locationHint',
    'customer.customerNumber',
    'customer.name',
    'customer.company',
    'customer.mailingAddress.street',
    'customer.mailingAddress.zip',
    'customer.mailingAddress.city',
    'customer.mailingAddress.postbox',
    'partner.partnerKey',
    'partner.name',
    'partner.partnerCompany',
    'partner.contractId',
    'meterRateTask.tariffName',
    'meterRateTask.unit',
    'meterRateTask.meterType',
    'meterRateTask.maloId',
    'meterRateTask.meloId',
    'job.externalIdentifier',
    'meterRateTask.validUntil',
    'meterRateTask.executionDeadline',
];

// Logisch zusammengehörende Felder bilden Varianz-Bündel: variiert eines,
// werden alle aufgenommen. Nur die PLZ ohne den Ort (oder umgekehrt) zu zeigen
// wäre verwirrend — Straße + Hausnummer sind bereits EINE Spalte (der
// Value-Resolver joint beide), deshalb brauchen nur PLZ + Ort ein Bündel.
const VARIANCE_BUNDLES: string[][] = [
    ['meter.address.zip', 'meter.address.city'],
    ['customer.mailingAddress.zip', 'customer.mailingAddress.city'],
    ['partner.mailingAddress.zip', 'partner.mailingAddress.city'],
];

function normalizeVarianceValue(value: ExportColumnValue): string {
    // Dates compared by timestamp so equal dates are not seen as variance.
    return value instanceof Date ? String(value.getTime()) : String(value ?? '');
}

// True if the resolved export value for `key` differs across any (job, task) pair.
// Empty counts as a value (so "some filled, some empty" is variance). Iterates over
// EVERY task (not just the first) and early-exits on the second distinct value.
export function hasVariance(key: string, jobs: JobType[]): boolean {
    let seen: string | undefined;
    for (const job of jobs) {
        const tasks = job.meter?.meterRateTasks?.length ? job.meter.meterRateTasks : [undefined];
        for (const task of tasks) {
            const normalized = normalizeVarianceValue(extractExportColumnValue(key, job, task));
            if (seen === undefined) {
                seen = normalized;
            } else if (normalized !== seen) {
                return true;
            }
        }
    }
    return false;
}

function resolveAutoColumns(jobs: JobType[], appConfig?: ApplicationConfig): ExcelColumnConfig[] {
    const varianceKeys = new Set(OPTIONAL_AUTO_COLUMN_KEYS.filter(key => hasVariance(key, jobs)));

    // Bündel vervollständigen: variiert z.B. die PLZ, kommt der Ort mit rein
    // (und umgekehrt). Leere Bündel-Partner entfernt das Schema-Pruning wieder.
    VARIANCE_BUNDLES.forEach(bundle => {
        if (bundle.some(key => varianceKeys.has(key))) {
            bundle.forEach(key => varianceKeys.add(key));
        }
    });

    const varianceColumns = OPTIONAL_AUTO_COLUMN_KEYS
        .filter(key => varianceKeys.has(key))
        .map(key => ({ key, visible: true, locked: true }));

    // Historische Lesestände: immer in Auto-Mode enthalten (auch nötig für Historic-Validierung)
    const historyColumns: ExcelColumnConfig[] = [
        { key: 'meterRateTask.lastReadingValue', visible: true, locked: true },
        { key: 'meterRateTask.lastReadingDate', visible: true, locked: true },
    ];

    // Ergebnis-Spalten: wenn mind. ein Auftrag Ergebnisse hat ODER Continuous-Validierung aktiv ist
    // (Continuous braucht die Vergleichsspalte im Sheet — sonst kann Excel nicht validieren).
    const hasResults = jobs.some(job =>
        job.meter.meterRateTasks.some(t => t.readingResults && t.readingResults.length > 0)
    );
    const continuousRequired = jobs.some(job =>
        mergeJobConfig(job.config, appConfig)?.features?.continuousReadingsValidationRequired === true
    );
    const resultColumns: ExcelColumnConfig[] = (hasResults || continuousRequired)
        ? [
            { key: 'meterRateTask.lastResultDate', visible: true, locked: true },
            { key: 'meterRateTask.lastResultValue', visible: true, locked: true },
          ]
        : [];

    return [...varianceColumns, ...historyColumns, ...resultColumns];
}

function sortByCanonicalOrder(columns: ExcelColumnConfig[]): ExcelColumnConfig[] {
    return [...columns].sort((a, b) => {
        const ai = CANONICAL_COLUMN_ORDER.indexOf(a.key);
        const bi = CANONICAL_COLUMN_ORDER.indexOf(b.key);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });
}

export function resolveExportConfig(
    jobs: JobType[],
    formatId: 'auto' | string,
    appConfig: ApplicationConfig,
    customColumns?: string[]
): ResolvedExportConfig {
    const baseFeatures: ExcelExportFeatures = { ...appConfig.excelExport?.features };

    if (formatId === AUTO_FORMAT_ID) {
        return {
            formatId,
            additionalColumns: resolveAutoColumns(jobs, appConfig),
            features: baseFeatures,
        };
    }

    if (formatId === CUSTOM_FORMAT_ID) {
        const cols: ExcelColumnConfig[] =
            customColumns && customColumns.length > 0
                ? customColumns.map(key => ({ key, visible: true, locked: true }))
                : [];
        return {
            formatId,
            additionalColumns: sortByCanonicalOrder(cols),
            features: baseFeatures,
        };
    }

    // Named format
    const format = appConfig.excelExport?.formats?.find(f => f.id === formatId);
    if (!format) {
        // Fall back to auto logic
        return {
            formatId,
            additionalColumns: resolveAutoColumns(jobs, appConfig),
            features: baseFeatures,
        };
    }

    return {
        formatId,
        additionalColumns: format.additionalColumns,
        features: { ...baseFeatures, ...format.features },
    };
}
